import {
  Accordion,
  AccordionBody,
  AccordionHeader,
  ButtonSection,
  DetailSection,
  LeaveDetailTable,
  SummarySubTitle,
} from '@/components/MenuDrawer/MyMenuDrawer/SubMenu/Vacation/styles';
import {
  Box,
  Button,
  Divider,
  IconButton,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { MoreVert as MoreVertIcon } from '@mui/icons-material';
import Skeleton from '@/components/@IntranetLibrary/Skeleton';
import useVacationProps from './@hooks/useVacationProps';
import classNames from 'classnames';
import Select from '@/components/@IntranetLibrary/Select';
import React, { useState } from 'react';
import ContentDescription from '@/components/MenuDrawer/MyMenuDrawer/@components/ContentDescription';
import AnnualLeaveModal from '@/components/MenuDrawer/MyMenuDrawer/SubMenu/WorkHours/AnnualLeaveModal';
import EmptyContent from '@/components/@IntranetLibrary/EmptyContent';
import LinkMenuIcon from '@/public/icons/link_menu.svg';
import { BoardDetailMenu } from '@/components/Board/BoardDetail/styles';
import { useDialog } from '@/hooks/global-tools';
import { formatLeaveMinutes } from '@/utils/leave';
import { LeaveCancelType } from '@/types/api';
import { useMypageEventTracker } from '@/hooks/gtm/useGTMEventTracker';
import { MypageEventName } from '@/constant/gtm';
import { useTranslation } from 'react-i18next';

const Vacation = () => {
  const { t } = useTranslation();
  const {
    status,
    prevYear,
    prevYearData,
    yearOptions,
    useAnnualLeaveModal,
    cancelLeave,
    moveToCancelApproval,
    refetchVacation,
    applyButtonOnClick,
    ...props
  } = useVacationProps();
  const mypageEventTracker = useMypageEventTracker();
  const dialog = useDialog();
  const [isLeaveModalOpen, setIsLeaveModalOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuDocumentId, setMenuDocumentId] = useState<string | null>(null);
  const [menuCancelType, setMenuCancelType] =
    useState<LeaveCancelType>('immediate');

  const errorMessage = t('에러가 발생했어요.');

  const trackApplyClick = () => {
    // GA Mypage - 23번
    mypageEventTracker(MypageEventName.clickEvent, {
      area_name: '휴가 현황 조회',
      contents: '연차 신청',
      status: undefined,
      description: '연차 신청',
    });
  };

  const handleKebabOpen = (
    event: React.MouseEvent<HTMLButtonElement>,
    documentId: string,
    cancelType: LeaveCancelType,
  ) => {
    setMenuAnchor(event.currentTarget);
    setMenuDocumentId(documentId);
    setMenuCancelType(cancelType);
  };

  const handleKebabClose = () => {
    setMenuAnchor(null);
    setMenuDocumentId(null);
  };

  const handleCancelClick = () => {
    const documentId = menuDocumentId;
    const cancelType = menuCancelType;
    handleKebabClose();
    if (!documentId || cancelType === 'none') return;
    // 즉시 취소와 Approval(취소 품의) 취소는 안내 알럿 메세지/버튼을 구분
    if (cancelType === 'approval') {
      dialog.show({
        type: 'confirm',
        message: (
          <>
            {t('MYPAGE.지난 근태 내역은 결재 후 취소됩니다.')}
            <br />
            {t('MYPAGE.취소 신청 품의를 작성하시겠습니까?')}
          </>
        ),
        confirmButtonText: t('MYPAGE.취소 품의 작성'),
        cancelButtonText: t('MYPAGE.닫기'),
        onConfirm: () => moveToCancelApproval(documentId),
      });
      return;
    }
    // immediate: 즉시 DELETE 취소
    dialog.show({
      type: 'confirm',
      message: t('MYPAGE.연차 신청을 취소하시겠습니까?'),
      confirmButtonText: t('MYPAGE.취소하기'),
      cancelButtonText: t('MYPAGE.유지하기'),
      onConfirm: () => cancelLeave(documentId),
    });
  };

  return (
    <>
      {status.status === 'loading' && (
        <Accordion>
          <AccordionHeader>
            <Skeleton variant={'text'} width="80%" height={24} />
          </AccordionHeader>
          <AccordionBody>
            <ContentDescription hasVerticalMargin={false} sx={{ gap: '4px' }}>
              <Skeleton variant={'text'} width={50} height={30} />
              <Skeleton variant={'text'} height={30} />
              <Skeleton variant={'text'} height={30} />
              <Skeleton variant={'text'} width={50} height={30} />
              <Skeleton variant={'text'} height={30} />
              <Skeleton variant={'text'} height={30} />
            </ContentDescription>
          </AccordionBody>
        </Accordion>
      )}
      {status.status === 'empty' && (
        <Box sx={{ margin: '28px 0' }}>
          <EmptyContent title={t('MYPAGE.연차 내역이 없습니다.')} />
        </Box>
      )}
      {status.status === 'error' && (
        <Box sx={{ margin: '28px 0' }}>
          <EmptyContent title={errorMessage} type={'error'} />
        </Box>
      )}
      {status.status === 'success' && (
        <Accordion>
          <AccordionHeader>
            {(() => {
              const remainingLabel = formatLeaveMinutes(
                status.data.remainingMinutes,
              );
              return (
                <div
                  dangerouslySetInnerHTML={{
                    __html: t('MYPAGE.현재 잔여 휴가는 {{value}} 입니다.', {
                      value: remainingLabel,
                    }).replace(
                      remainingLabel,
                      `<span class="primary">${remainingLabel}</span>`,
                    ),
                  }}
                />
              );
            })()}
          </AccordionHeader>
          <AccordionBody>
            <ContentDescription
              hasVerticalMargin={false}
              sx={{ padding: '16px 12px' }}
            >
              {status.data.monthly && (
                <>
                  <SummarySubTitle>{t('MYPAGE.월차')}</SummarySubTitle>
                  <ListItem
                    items={[
                      {
                        title: t('MYPAGE.부여'),
                        content: `+${formatLeaveMinutes(
                          status.data.monthly.grantedMinutes,
                        )}`,
                      },
                      {
                        title: t('MYPAGE.사용'),
                        content: `-${formatLeaveMinutes(
                          status.data.monthly.usedMinutes,
                        )}`,
                        isRed: true,
                      },
                    ]}
                  />
                </>
              )}
              {status.data.annual && (
                <>
                  <SummarySubTitle>{t('MYPAGE.연차')}</SummarySubTitle>
                  <ListItem
                    items={[
                      {
                        title: t('MYPAGE.부여'),
                        content: `+${formatLeaveMinutes(
                          status.data.annual.grantedMinutes,
                        )}`,
                      },
                      {
                        title: t('MYPAGE.사용'),
                        content: `-${formatLeaveMinutes(
                          status.data.annual.usedMinutes,
                        )}`,
                        isRed: true,
                      },
                    ]}
                  />
                </>
              )}
              {status.data.overageMinutes > 0 && (
                <ListItem
                  items={[
                    {
                      title: t('MYPAGE.전년 초과 사용'),
                      content: `-${formatLeaveMinutes(
                        status.data.overageMinutes,
                      )}`,
                      isRed: true,
                    },
                  ]}
                />
              )}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  paddingLeft: '8px',
                  borderTop: '1px solid var(--dk-text, #000)',
                  paddingTop: '12px',
                }}
              >
                <Typography
                  sx={{
                    flex: 1,
                    fontSize: '14px',
                    lineHeight: '20px',
                    fontWeight: 700,
                    color: 'var(--dk-text, #000)',
                  }}
                >
                  {t('MYPAGE.잔여휴가')}
                </Typography>
                <Typography
                  sx={{
                    flex: 'none',
                    fontSize: '20px',
                    lineHeight: '28px',
                    fontWeight: 700,
                    color: 'var(--dk-accent, #0066CC)',
                  }}
                >
                  {formatLeaveMinutes(status.data.remainingMinutes)}
                </Typography>
              </Box>
            </ContentDescription>
          </AccordionBody>
        </Accordion>
      )}

      <ButtonSection>
        {useAnnualLeaveModal ? (
          <Button
            variant={'contained'}
            size={'large'}
            onClick={() => {
              setIsLeaveModalOpen(true);
              trackApplyClick();
            }}
            // startIcon={<LinkMenuIcon width="20" height="20" stroke="white" />}
          >
            {t('MYPAGE.연차 신청')}
          </Button>
        ) : (
          <Button
            variant={'contained'}
            size={'large'}
            onClick={() => {
              applyButtonOnClick();
              trackApplyClick();
            }}
            startIcon={<LinkMenuIcon width="20" height="20" stroke="white" />}
          >
            {t('MYPAGE.연차 신청')}
          </Button>
        )}
      </ButtonSection>

      <DetailSection>
        <Divider sx={{ mb: '24px' }} />
        <Box sx={{ display: 'flex', width: '100%', mb: '8px' }}>
          <Stack flex={1} gap={'4px'}>
            <Typography
              sx={{
                color: 'var(--dk-text, #000)',
                fontSize: '14px',
                fontWeight: 700,
                lineHeight: '20px',
              }}
            >
              {t('MYPAGE.{{year}}년 휴가사용 상세내역', { year: prevYear })}
            </Typography>
            <Typography
              sx={{
                color: 'var(--dk-text-secondary, #888D96)',
                fontSize: '12px',
                fontWeight: 400,
                lineHeight: '18px',
              }}
            >
              {t('MYPAGE.연도별 기준 휴가 발급 및 사용내역')}
            </Typography>
          </Stack>
          <Select
            options={yearOptions}
            value={`${prevYear}`}
            onChange={props.yearOnChange}
            disablePortal={true}
            sx={{ minWidth: '100px' }}
          />
        </Box>
        <LeaveDetailTable>
          <thead>
            <tr>
              <th className="col-date">{t('MYPAGE.사용일자')}</th>
              <th className="col-type">{t('MYPAGE.분류')}</th>
              <th className="col-amount">{t('MYPAGE.수')}</th>
              <th className="col-kebab" />
            </tr>
          </thead>
          {prevYearData.status === 'error' && (
            <tbody>
              <tr>
                <td colSpan={4} style={{ padding: '50px 0' }}>
                  <EmptyContent title={errorMessage} type={'error'} />
                </td>
              </tr>
            </tbody>
          )}
          {prevYearData.status === 'loading' && (
            <tbody>
              {[0, 1, 2, 3, 4, 5].map((item) => (
                <tr key={item}>
                  <td colSpan={4}>
                    <Skeleton variant={'text'} />
                  </td>
                </tr>
              ))}
            </tbody>
          )}
          {prevYearData.status === 'success' && (
            <tbody>
              {prevYearData.data.length > 0 ? (
                prevYearData.data.map((row, index) => (
                  <tr
                    key={index}
                    className={classNames({
                      red: row.rowType === 'overage',
                      granted: row.rowType === 'granted',
                    })}
                  >
                    <td
                      className={classNames('col-date', { link: row.hasLink })}
                      onClick={row.onClick}
                    >
                      {row.date}
                    </td>
                    <td className="col-type">{row.title}</td>
                    <td className="col-amount">{row.detail}</td>
                    <td className="col-kebab">
                      {row.rowType === 'usage' && row.documentId && (
                        <IconButton
                          size="small"
                          onClick={(e) =>
                            handleKebabOpen(
                              e,
                              row.documentId as string,
                              (row.cancelType ?? 'none') as LeaveCancelType,
                            )
                          }
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4} style={{ textAlign: 'center' }}>
                    {t('MYPAGE.발급된 휴가가 없습니다.')}
                  </td>
                </tr>
              )}
            </tbody>
          )}
        </LeaveDetailTable>

        <BoardDetailMenu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={handleKebabClose}
          MenuListProps={{ sx: { width: '160px' } }}
        >
          <Tooltip
            title={
              menuCancelType === 'none'
                ? // 문구의 ':' 가 i18next ns 구분자로 해석되는 것 방지 (nsSeparator:false)
                  t('MYPAGE.이미 정산/소멸된 항목입니다. 문의: HR팀', {
                    nsSeparator: false,
                  })
                : ''
            }
            placement="top"
          >
            <span style={{ display: 'block' }}>
              <MenuItem
                disabled={menuCancelType === 'none'}
                style={{ color: menuCancelType === 'none' ? '#ccc' : 'red' }}
                sx={{
                  padding: '6px',
                  '& .MuiTouchRipple-root': { color: 'var(--dk-text, black)' },
                }}
                onClick={handleCancelClick}
              >
                {t('MYPAGE.취소 신청')}
              </MenuItem>
            </span>
          </Tooltip>
        </BoardDetailMenu>
      </DetailSection>

      <AnnualLeaveModal
        open={isLeaveModalOpen}
        onClose={() => setIsLeaveModalOpen(false)}
        onSuccess={refetchVacation}
      />
    </>
  );
};

export default Vacation;

const ListItem = ({
  items,
}: {
  items: { title: string; content: string; isRed?: boolean }[];
}) => {
  return (
    <Stack gap={'8px'}>
      {items.map(({ title, content, isRed }, index) => (
        <Box
          key={index}
          sx={{
            display: 'flex',
            alignItems: 'center',
            paddingLeft: '8px',
          }}
        >
          <Typography
            sx={{
              flex: 1,
              fontSize: '14px',
              lineHeight: '20px',
              fontWeight: 400,
              color: 'var(--dk-text-secondary, #888D96)',
            }}
          >
            {title}
          </Typography>
          <Typography
            sx={{
              flex: 'none',
              fontSize: '16px',
              lineHeight: '24px',
              fontWeight: 400,
              color: isRed
                ? 'var(--dk-danger, #F55060)'
                : 'var(--dk-text, #000000)',
            }}
          >
            {content}
          </Typography>
        </Box>
      ))}
    </Stack>
  );
};
